import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type MouseEvent,
} from "react";
import { DICT, type DictKey, type Lang } from "./_home/dict";
import { formatPrice, type Offer } from "./lib/products";
import { submitLead } from "./lib/api";
import { homeHref, isPlainClick } from "./lib/nav";

interface TripPageProps {
  slug: string;
  lang: Lang;
  onBack: () => void;
  /** Offers list already fetched by App.tsx — passed in to avoid a duplicate /products request. */
  offers: Offer[] | null;
  /** True if the App.tsx fetch errored. */
  offersError: boolean;
}

/**
 * Scrolls to a same-page section by id WITHOUT touching window.location.hash.
 * Important: the App-level router uses the hash to detect trip routes
 * (#trip/<slug>). Letting an in-page anchor (#welcome, #inquire) overwrite
 * the hash would fire the hashchange listener and navigate back home.
 */
function scrollToId(id: string, behavior: ScrollBehavior = "smooth") {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior, block: "start" });
}

/**
 * Take a parsed section and return display lines. Prefers explicit `-` bullets;
 * if the editor wrote a paragraph instead, splits on newlines so we still
 * render their words instead of silently falling back to dict placeholders.
 */
function pickLines(section: {
  bullets?: string[];
  body?: string;
} | undefined): string[] {
  if (!section) return [];
  if (section.bullets && section.bullets.length > 0) return section.bullets;
  if (!section.body) return [];
  return section.body
    .split("\n")
    .map((l) => l.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);
}

interface SubState {
  busy: boolean;
  msg: { kind: "ok" | "err"; text: string } | null;
}

export default function TripPage({
  slug,
  lang,
  onBack,
  offers,
  offersError,
}: TripPageProps) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sub, setSub] = useState<SubState>({ busy: false, msg: null });

  const t = (key: DictKey) => DICT[lang][key];

  const trip = useMemo<Offer | null>(() => {
    if (!offers) return null;
    return offers.find((o) => o.slug === slug || o.id === slug) ?? null;
  }, [offers, slug]);

  // Honour prefers-reduced-motion when scrolling between in-page sections.
  const reduceMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  // Single click handler for every in-page anchor (#welcome, #inquire, …).
  // Prevents the default jump-to-hash that would otherwise wipe out the
  // trip route in window.location.hash.
  const handleAnchor = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const href = e.currentTarget.getAttribute("href") ?? "";
      if (!href.startsWith("#") || href.length < 2) return;
      e.preventDefault();
      scrollToId(href.slice(1), reduceMotion ? "auto" : "smooth");
    },
    [reduceMotion],
  );

  // "Back" returns to the homepage — a real link (homeHref) so it works
  // without JS / opens in a new tab, with a plain-click intercept for the
  // instant in-app transition.
  const onBackClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (!isPlainClick(e)) return;
      e.preventDefault();
      onBack();
    },
    [onBack],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (offersError) {
    return (
      <main className="trip-empty">
        <p>{t("trip_load_err")}</p>
        <a className="trip-back" href={homeHref(lang)} onClick={onBackClick}>
          ← {t("trip_back")}
        </a>
      </main>
    );
  }

  if (offers === null) {
    return (
      <main className="trip-empty">
        <p>{t("offers_loading")}</p>
      </main>
    );
  }

  if (!trip) {
    return (
      <main className="trip-empty">
        <p>{t("trip_not_found")}</p>
        <a className="trip-back" href={homeHref(lang)} onClick={onBackClick}>
          ← {t("trip_back")}
        </a>
      </main>
    );
  }

  const onInquire = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || sub.busy) return;
    setSub({ busy: true, msg: null });
    try {
      const res = await submitLead({
        email,
        source: `trip:${trip.slug ?? trip.id}`,
        locale: lang,
        // tripInterest stays bounded to the trip identifier (slug or short
        // name). Free-form text rides in `note` so it can't blow the
        // 200-char cap or pollute the HL tag namespace.
        tripInterest: (trip.slug ?? trip.name).slice(0, 200),
        note: note || undefined,
        tags: ["trip-inquiry", `trip:${trip.slug ?? trip.id}`],
      });
      if (res.ok) {
        setSub({ busy: false, msg: { kind: "ok", text: t("trip_form_ok") } });
        setEmail("");
        setNote("");
      } else {
        setSub({ busy: false, msg: { kind: "err", text: t("foot_sub_err") } });
      }
    } catch {
      setSub({ busy: false, msg: { kind: "err", text: t("foot_sub_err") } });
    }
  };

  const heroImg = trip.image ?? trip.medias[0];
  const galleryImgs = trip.medias.slice(0, 8);

  // Content sourced from joined Custom Object Trip record for current locale,
  // falling back to dict.ts placeholders if a section isn't authored yet.
  // Falls back to the English content when the current locale's parsed
  // sections are empty (e.g. only EN is authored).
  const tripContent = trip.trip?.content;
  const localized = tripContent?.[lang];
  const content =
    localized && localized.order.length > 0 ? localized : tripContent?.en;
  const secOverview = content?.sections.overview;
  const secHighlights = content?.sections.highlights;
  const secDay = content?.sections.day;
  const secIncluded = content?.sections.included;
  const secNotIncluded = content?.sections.not_included;
  const secFaq = content?.sections.faq;
  const secWelcomeName = content?.sections.welcome_name;
  const durationDays = trip.trip?.durationDays;
  const groupMax = trip.trip?.groupMax;
  // Welcome short-name: prefer an editor-authored `## welcome_name`, fall
  // back to the whole product name (no longer truncated at `&`).
  const welcomeName = secWelcomeName?.body?.trim() || trip.name;

  return (
    <main className="trip">
      {/* Hero */}
      <section
        className="trip-hero"
        style={heroImg ? { backgroundImage: `url('${heroImg}')` } : undefined}
      >
        <div className="trip-hero-scrim" />
        <div className="trip-hero-inner">
          <a
            className="trip-back trip-back--floating"
            href={homeHref(lang)}
            onClick={onBackClick}
          >
            ← {t("trip_back")}
          </a>
          <div className="trip-eyebrow">{t("trip_eyebrow")}</div>
          <h1 className="trip-title">{trip.name}</h1>
          <div className="trip-strip">
            {trip.price && (
              <div className="trip-strip-item">
                <span className="trip-strip-k">{formatPrice(trip.price, lang)}</span>
                <span className="trip-strip-l">{t("trip_price_label")}</span>
              </div>
            )}
            <div className="trip-strip-item">
              <span className="trip-strip-k">
                {durationDays != null
                  ? `${durationDays} ${durationDays === 1 ? (lang === "es" ? "día" : "day") : lang === "es" ? "días" : "days"}`
                  : t("trip_duration_value")}
              </span>
              <span className="trip-strip-l">{t("trip_duration_label")}</span>
            </div>
            <div className="trip-strip-item">
              <span className="trip-strip-k">
                {groupMax != null
                  ? `${groupMax} ${lang === "es" ? "máx." : "max"}`
                  : t("trip_group_value")}
              </span>
              <span className="trip-strip-l">{t("trip_group_label")}</span>
            </div>
          </div>
          <a className="btn-primary" href="#inquire" onClick={handleAnchor}>
            <span>{t("trip_cta_primary")}</span>
          </a>
        </div>
      </section>

      {/* Section nav */}
      <nav className="trip-secnav" aria-label={t("trip_secnav_aria")}>
        <a href="#welcome" onClick={handleAnchor}>{t("trip_sec_welcome")}</a>
        <a href="#love" onClick={handleAnchor}>{t("trip_sec_love")}</a>
        <a href="#day-by-day" onClick={handleAnchor}>{t("trip_sec_day")}</a>
        <a href="#notes" onClick={handleAnchor}>{t("trip_sec_notes")}</a>
        <a href="#gallery" onClick={handleAnchor}>{t("trip_sec_gallery")}</a>
        <a href="#faq" onClick={handleAnchor}>{t("trip_sec_faq")}</a>
      </nav>

      {/* Body — main column + sticky sidebar */}
      <div className="trip-layout">
        <div className="trip-main">

      {/* Welcome */}
      <section className="trip-block trip-welcome" id="welcome">
        <h2>
          <span>{t("trip_welcome_t1")}</span>{" "}
          <span className="it">{welcomeName}.</span>
        </h2>
        <p className="trip-lede">{secOverview?.body ?? trip.description}</p>
      </section>

      {/* What you'll love */}
      <section className="trip-block trip-love" id="love">
        <h2>
          <span>{t("trip_love_t1")}</span>
          <span className="it">{t("trip_love_t2")}</span>
        </h2>
        <div className="trip-love-grid">
          {(() => {
            const explicit = (secHighlights?.bullets ?? []).filter(Boolean);
            // If the editor wrote a paragraph instead of bullets, split it
            // into lines so we still render their words (not dict).
            const fromBody = explicit.length === 0 && secHighlights?.body
              ? secHighlights.body
                  .split("\n")
                  .map((l) => l.replace(/^[-•*]\s*/, "").trim())
                  .filter(Boolean)
              : [];
            const lines = explicit.length > 0 ? explicit : fromBody;
            if (lines.length > 0) {
              return lines.slice(0, 6).map((line, i) => (
                <article key={i} className="trip-love-card">
                  <div className="trip-love-num">0{i + 1}</div>
                  <p>{line}</p>
                </article>
              ));
            }
            return [1, 2, 3, 4].map((n) => {
              const titleKey = `trip_love_${n}_t` as DictKey;
              const bodyKey = `trip_love_${n}_p` as DictKey;
              return (
                <article key={n} className="trip-love-card">
                  <div className="trip-love-num">0{n}</div>
                  <h3>{t(titleKey)}</h3>
                  <p>{t(bodyKey)}</p>
                </article>
              );
            });
          })()}
        </div>
      </section>

      {/* Day by day */}
      <section className="trip-block trip-day" id="day-by-day">
        <h2>
          <span>{t("trip_day_t1")}</span>
          <span className="it">{t("trip_day_t2")}</span>
        </h2>
        <ol className="trip-day-list">
          {secDay?.items && secDay.items.length > 0
            ? secDay.items.map((item, i) => (
                <li key={i} className="trip-day-item">
                  <div className="trip-day-marker">
                    <span className="trip-day-num">
                      {t("trip_day_label")} {i + 1}
                    </span>
                  </div>
                  <div className="trip-day-content">
                    <h3>{item.title}</h3>
                    {item.body && <p>{item.body}</p>}
                  </div>
                </li>
              ))
            : [1, 2, 3, 4, 5].map((n) => {
                const titleKey = `trip_day_${n}_t` as DictKey;
                const bodyKey = `trip_day_${n}_p` as DictKey;
                return (
                  <li key={n} className="trip-day-item">
                    <div className="trip-day-marker">
                      <span className="trip-day-num">
                        {t("trip_day_label")} {n}
                      </span>
                    </div>
                    <div className="trip-day-content">
                      <h3>{t(titleKey)}</h3>
                      <p>{t(bodyKey)}</p>
                    </div>
                  </li>
                );
              })}
        </ol>
        <p className="trip-day-foot">{t("trip_day_foot")}</p>
      </section>

      {/* Trip notes */}
      <section className="trip-block trip-notes" id="notes">
        <h2>
          <span>{t("trip_notes_t1")}</span>
          <span className="it">{t("trip_notes_t2")}</span>
        </h2>
        <div className="trip-notes-grid">
          <div>
            <h3>{t("trip_notes_in_t")}</h3>
            <ul>
              {(() => {
                const lines = pickLines(secIncluded);
                if (lines.length > 0) return lines.map((line, i) => <li key={i}>{line}</li>);
                return [1, 2, 3, 4, 5].map((n) => (
                  <li key={n}>{t(`trip_notes_in_${n}` as DictKey)}</li>
                ));
              })()}
            </ul>
          </div>
          <div>
            <h3>{t("trip_notes_out_t")}</h3>
            <ul>
              {(() => {
                const lines = pickLines(secNotIncluded);
                if (lines.length > 0) return lines.map((line, i) => <li key={i}>{line}</li>);
                return [1, 2, 3].map((n) => (
                  <li key={n}>{t(`trip_notes_out_${n}` as DictKey)}</li>
                ));
              })()}
            </ul>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {galleryImgs.length > 0 && (
        <section className="trip-block trip-gallery" id="gallery">
          <h2>
            <span>{t("trip_gallery_t1")}</span>
            <span className="it">{t("trip_gallery_t2")}</span>
          </h2>
          <div className="trip-gallery-grid">
            {galleryImgs.map((src, i) => (
              <div
                key={`${src}-${i}`}
                className="trip-gallery-tile"
                style={{ backgroundImage: `url('${src}')` }}
                role="img"
                aria-label={`${trip.name} — ${i + 1}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="trip-block trip-faq" id="faq">
        <h2>
          <span>{t("trip_faq_t1")}</span>
          <span className="it">{t("trip_faq_t2")}</span>
        </h2>
        <div className="trip-faq-list">
          {(() => {
            if (secFaq?.items && secFaq.items.length > 0) {
              return secFaq.items.map((item, i) => (
                <details key={i} className="trip-faq-item">
                  <summary>{item.title}</summary>
                  {item.body && <p>{item.body}</p>}
                </details>
              ));
            }
            // If the editor authored a paragraph FAQ instead of ### items,
            // surface it as a single open block rather than dropping to dict.
            if (secFaq?.body) {
              return (
                <details className="trip-faq-item" open>
                  <summary>{t("trip_faq_1_q")}</summary>
                  <p>{secFaq.body}</p>
                </details>
              );
            }
            return [1, 2, 3, 4].map((n) => {
              const q = `trip_faq_${n}_q` as DictKey;
              const a = `trip_faq_${n}_a` as DictKey;
              return (
                <details key={n} className="trip-faq-item">
                  <summary>{t(q)}</summary>
                  <p>{t(a)}</p>
                </details>
              );
            });
          })()}
        </div>
      </section>

        </div>

        {/* Sticky sidebar — Trip Details + Booking module */}
        <aside className="trip-side" aria-label={t("trip_side_title")}>
          <div className="trip-side-card">
            <div className="trip-side-head">
              <span className="trip-side-eyebrow">{t("trip_side_title")}</span>
            </div>
            <dl className="trip-side-rows">
              {trip.price && (
                <div className="trip-side-row trip-side-row--primary">
                  <dt>{t("trip_side_from")}</dt>
                  <dd>{formatPrice(trip.price, lang)}</dd>
                </div>
              )}
              <div className="trip-side-row">
                <dt>{t("trip_side_duration")}</dt>
                <dd>
                  {durationDays != null
                    ? `${durationDays} ${durationDays === 1 ? (lang === "es" ? "día" : "day") : lang === "es" ? "días" : "days"}`
                    : t("trip_duration_value")}
                </dd>
              </div>
              <div className="trip-side-row">
                <dt>{t("trip_side_group")}</dt>
                <dd>
                  {groupMax != null
                    ? `${groupMax} ${lang === "es" ? "máx." : "max"}`
                    : t("trip_group_value")}
                </dd>
              </div>
              <div className="trip-side-row">
                <dt>{t("trip_side_departs")}</dt>
                <dd>{t("trip_side_departs_value")}</dd>
              </div>
              <div className="trip-side-row">
                <dt>{t("trip_side_languages")}</dt>
                <dd>{t("trip_side_languages_value")}</dd>
              </div>
              <div className="trip-side-row">
                <dt>{t("trip_side_inclusive")}</dt>
                <dd>{t("trip_side_inclusive_value")}</dd>
              </div>
            </dl>
            <a className="trip-side-cta btn-primary" href="#inquire" onClick={handleAnchor}>
              <span>{t("trip_side_book_cta")}</span>
            </a>
            <a className="trip-side-sub" href="#inquire" onClick={handleAnchor}>
              {t("trip_side_book_sub")}
            </a>
            <div className="trip-side-foot">
              <span className="trip-side-foot-k">{t("trip_side_seats")}</span>
              <span className="trip-side-foot-l">{t("trip_side_seats_value")}</span>
            </div>
          </div>
        </aside>
      </div>

      {/* Inquiry — one-to-one trip enquiry, not a newsletter signup. */}
      <section className="trip-block trip-inquire" id="inquire">
        <h2>
          <span>{t("trip_inq_t1")}</span>
          <span className="it">{trip.name}.</span>
        </h2>
        <p className="trip-inq-deck">{t("trip_inq_deck")}</p>
        <form className="trip-inq-form" onSubmit={onInquire}>
          <input
            className="trip-inq-input"
            type="email"
            required
            placeholder={t("trip_inq_email_ph")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={sub.busy}
          />
          <textarea
            className="trip-inq-note"
            rows={3}
            placeholder={t("trip_inq_note_ph")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={sub.busy}
            maxLength={500}
          />
          <button className="btn-primary trip-inq-submit" type="submit" disabled={sub.busy}>
            {sub.busy ? "…" : t("trip_inq_btn")}
          </button>
        </form>
        {sub.msg && (
          <p className={`trip-inq-msg${sub.msg.kind === "err" ? " is-err" : ""}`}>
            {sub.msg.text}
          </p>
        )}
      </section>

      {/* Sticky bottom bar */}
      <div className="trip-stickybar">
        <div className="trip-stickybar-l">
          <span className="trip-stickybar-name">{trip.name}</span>
          {trip.price && (
            <span className="trip-stickybar-price">
              {formatPrice(trip.price, lang)}
            </span>
          )}
        </div>
        <a className="btn-primary" href="#inquire" onClick={handleAnchor}>
          <span>{t("trip_cta_primary")}</span>
        </a>
      </div>
    </main>
  );
}
